import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { 
  User, Mail, Building, AlertCircle, CheckCircle, 
  Clock, Globe, Award, BookOpen, ChevronRight, 
  ArrowLeft, ArrowRight, Info, Shield
} from 'lucide-react';
import './TestSetup.css';

const TestSetup = () => {
  const navigate = useNavigate();
  const { examId } = useParams();
  const location = useLocation();
  
  const [collegeStatus, setCollegeStatus] = useState(null); 
  
  // State management
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [collegeError, setCollegeError] = useState('');
  const [instructionsAccepted, setInstructionsAccepted] = useState(false);
  
  const exam = location.state?.exam || JSON.parse(localStorage.getItem("exam_info"));

  // FIXED: Memoized examInfo to prevent recreation on every render
  const examInfo = useMemo(() => ({
    name: exam?.examName || "Typing Skill Assessment",
    duration: exam?.duration || "10 minutes",
    language: exam?.language || "English",
    requirement: exam?.requirement || "25 WPM",
    totalMarks: exam?.totalMarks || "100",
    examCode: exam?.examCode || examId
  }), [exam, examId]);
  
  const [formData, setFormData] = useState({
    fullName: '',
    rollNumber: '',
    email: '',
    collegeCode: ''
  });
  
  // REMOVED: All validation states (errors and touched)
  
  // Handle input changes - ONLY update form data
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    setFormData(prev => ({
      ...prev,
      [name]: name === 'collegeCode' ? value.toUpperCase() : value
    }));
  };

  // REMOVED: All validation functions and blur handlers

  // Check if registration form is valid (all fields filled)
  const isRegistrationValid = useMemo(() => {
    return (
      formData.fullName.trim() !== '' &&
      formData.rollNumber.trim() !== '' &&
      formData.email.trim() !== '' &&
      formData.collegeCode.trim() !== ''
    );
  }, [formData]);

  // Handle registration completion
  const handleContinueToConfirmation = useCallback(() => {
    if (isRegistrationValid) {
      setCurrentStep(3);
    }
  }, [isRegistrationValid]);

  // FIXED: Optimized college verification useEffect
  useEffect(() => {
    if (!formData.collegeCode || formData.collegeCode.length < 6) {
      setCollegeStatus(null);
      return;
    }
    
    let isActive = true;
    const controller = new AbortController();
    
    const timer = setTimeout(async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_API_URL}/verify-college`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ collegeCode: formData.collegeCode }),
          signal: controller.signal
        });
        const data = await res.json();
        if (isActive) {
          setCollegeStatus(data.success ? "valid" : "invalid");
        }
      } catch {
        if (isActive) setCollegeStatus("invalid");
      }
    }, 800);
    
    return () => {
      isActive = false;
      clearTimeout(timer);
      controller.abort();
    };
  }, [formData.collegeCode]);

  // FIXED: Memoized handleStartExam
  const handleStartExam = useCallback(async () => {
    setLoading(true);
    setCollegeError('');
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/verify-college`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ 
          collegeCode: formData.collegeCode 
        }),
      });
      const data = await response.json();
      if (data.success) {
        localStorage.setItem("exam_info", JSON.stringify(exam));
        localStorage.setItem(
          'exam_registration',
          JSON.stringify({
            fullName: formData.fullName.trim(),
            rollNumber: formData.rollNumber.trim(),
            email: formData.email.trim(),
            institutionId: data.institutionId,
            collegeCode: formData.collegeCode,
            examId: examId,
            registeredAt: new Date().toISOString()
          })
        );
        navigate(`/exam/${examId}`, {
          state: { exam }
        });
      } else {
        setCollegeError(data.message || "Invalid college code. Please verify and try again.");
        setCurrentStep(2);
      }
    } catch (error) {
      setCollegeError("Unable to verify. Please check your connection and try again.");
      setCurrentStep(2);
    } finally {
      setLoading(false);
    }
  }, [formData, examId, exam, navigate]);

  // Progress indicator - memoized as JSX
  const ProgressIndicator = useMemo(() => {
    return (
      <div className="exam-form-progress">
        <div className="exam-form-steps">
          <div className={`exam-form-step ${currentStep >= 1 ? 'active' : ''} ${currentStep > 1 ? 'completed' : ''}`}>
            <div className="exam-form-step-number">
              {currentStep > 1 ? <CheckCircle size={16} /> : '01'}
            </div>
            <span className="exam-form-step-label">Instructions</span>
          </div>
          <div className="exam-form-step-line"></div>
          <div className={`exam-form-step ${currentStep >= 2 ? 'active' : ''} ${currentStep > 2 ? 'completed' : ''}`}>
            <div className="exam-form-step-number">
              {currentStep > 2 ? <CheckCircle size={16} /> : '02'}
            </div>
            <span className="exam-form-step-label">Registration</span>
          </div>
          <div className="exam-form-step-line"></div>
          <div className={`exam-form-step ${currentStep >= 3 ? 'active' : ''}`}>
            <div className="exam-form-step-number">03</div>
            <span className="exam-form-step-label">Confirmation</span>
          </div>
        </div>
      </div>
    );
  }, [currentStep]);

  // ExamInfoSidebar - memoized as JSX
  const ExamInfoSidebar = useMemo(() => {
    return (
      <div className="exam-form-sidebar">
        <div className="exam-form-sidebar-sticky">
          <div className="exam-form-sidebar-header">
            <BookOpen size={20} />
            <h3>Exam Information</h3>
          </div>
          
          <div className="exam-form-sidebar-content">
            <div className="exam-form-info-item">
              <span className="exam-form-info-label">Exam Name</span>
              <span className="exam-form-info-value">{examInfo.name}</span>
            </div>
            
            <div className="exam-form-info-item">
              <span className="exam-form-info-label">
                <Clock size={14} /> Duration
              </span>
              <span className="exam-form-info-value">{examInfo.duration}</span>
            </div>
            
            <div className="exam-form-info-item">
              <span className="exam-form-info-label">
                <Globe size={14} /> Language
              </span>
              <span className="exam-form-info-value">{examInfo.language}</span>
            </div>
            
            <div className="exam-form-info-item">
              <span className="exam-form-info-label">
                <Award size={14} /> Requirement
              </span>
              <span className="exam-form-info-value">{examInfo.requirement}</span>
            </div>
            
            <div className="exam-form-info-item">
              <span className="exam-form-info-label">
                <Award size={14} /> Total Marks
              </span>
              <span className="exam-form-info-value">{examInfo.totalMarks}</span>
            </div>
          </div>
          
          <div className="exam-form-sidebar-footer">
            <div className="exam-form-sidebar-note">
              <Info size={14} />
              <span>Keep your college code ready for verification</span>
            </div>
          </div>
        </div>
      </div>
    );
  }, [examInfo]);

  // Step 1: Instructions
  const renderInstructionsStep = () => (
    <div className="exam-form-section">
      <div className="exam-form-section-header">
        <h2 className="exam-form-section-title">Exam Instructions</h2>
        <p className="exam-form-section-subtitle">Please read all instructions carefully before proceeding</p>
      </div>

      <div className="exam-form-instructions-grid">
        <div className="exam-form-instruction-block">
          <h3 className="exam-form-instruction-heading">
            <Shield size={18} />
            Exam Guidelines
          </h3>
          <ul className="exam-form-instruction-list">
            <li>The typing test will begin immediately after registration</li>
            <li>Test ends automatically when the paragraph is completed</li>
            <li>Your typing speed and accuracy will be recorded</li>
            <li>Minimum typing speed requirement: {examInfo.requirement}</li>
          </ul>
        </div>

        <div className="exam-form-instruction-block">
          <h3 className="exam-form-instruction-heading">
            <AlertCircle size={18} />
            Important Rules
          </h3>
          <ul className="exam-form-instruction-list">
            <li>Do not switch browser tabs during the test</li>
            <li>Do not refresh the page once the test starts</li>
            <li>Copy-paste functionality is disabled</li>
            <li>Backspace and navigation keys are restricted</li>
          </ul>
        </div>

        <div className="exam-form-instruction-block">
          <h3 className="exam-form-instruction-heading">
            <CheckCircle size={18} />
            Technical Requirements
          </h3>
          <ul className="exam-form-instruction-list">
            <li>Stable internet connection required</li>
            <li>Use a desktop or laptop computer</li>
            <li>Chrome, Firefox, or Edge browser recommended</li>
            <li>Enable JavaScript in your browser</li>
          </ul>
        </div>

        <div className="exam-form-instruction-block exam-form-warning-block">
          <h3 className="exam-form-instruction-heading">
            <AlertCircle size={18} />
            Important Notice
          </h3>
          <p className="exam-form-warning-text">
            Violation of any rules may result in automatic test termination and disqualification from the examination process.
          </p>
        </div>
      </div>

      <div className="exam-form-checkbox-group">
        <label className="exam-form-checkbox-label">
          <input
            type="checkbox"
            checked={instructionsAccepted}
            onChange={(e) => setInstructionsAccepted(e.target.checked)}
            className="exam-form-checkbox"
          />
          <span className="exam-form-checkbox-text">
            I have read and understood all the instructions and agree to abide by the exam rules *
          </span>
        </label>
      </div>

      <div className="exam-form-actions">
        <button
          onClick={() => setCurrentStep(2)}
          disabled={!instructionsAccepted}
          className="exam-form-button exam-form-button-primary"
        >
          <span>Continue to Registration</span>
          <ArrowRight size={18} />
        </button>
      </div>
    </div>
  );

  // Step 2: Registration - REMOVED all validation
  const renderRegistrationStep = () => (
    <div className="exam-form-section">
      <div className="exam-form-section-header">
        <h2 className="exam-form-section-title">Candidate Registration</h2>
        <p className="exam-form-section-subtitle">Fill in your details exactly as per your institution records</p>
      </div>

      <div className="exam-form-fields">
        {/* Candidate Information */}
        <div className="exam-form-field-group">
          <h3 className="exam-form-field-group-title">Candidate Information</h3>
          
          <div className="exam-form-field">
            <label htmlFor="fullName" className="exam-form-label">
              Full Name <span className="exam-form-required">*</span>
            </label>
            <div className="exam-form-input-wrapper">
              <User size={16} className="exam-form-input-icon" />
              <input
                type="text"
                id="fullName"
                name="fullName"
                value={formData.fullName}
                onChange={handleInputChange}
                placeholder="Enter your full name"
                className="exam-form-input"
                disabled={loading}
                required
              />
            </div>
          </div>

          <div className="exam-form-field">
            <label htmlFor="rollNumber" className="exam-form-label">
              Roll Number <span className="exam-form-required">*</span>
            </label>
            <div className="exam-form-input-wrapper">
              <User size={16} className="exam-form-input-icon" />
              <input
                type="text"
                id="rollNumber"
                name="rollNumber"
                value={formData.rollNumber}
                onChange={handleInputChange}
                placeholder="Enter your roll number"
                className="exam-form-input"
                disabled={loading}
                required
              />
            </div>
          </div>
        </div>

        {/* Institution Verification */}
        <div className="exam-form-field-group">
          <h3 className="exam-form-field-group-title">Institution Verification</h3>
          
          <div className="exam-form-field">
            <label htmlFor="email" className="exam-form-label">
              Email Address <span className="exam-form-required">*</span>
            </label>
            <div className="exam-form-input-wrapper">
              <Mail size={16} className="exam-form-input-icon" />
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                placeholder="student@institution.edu"
                className="exam-form-input"
                disabled={loading}
                required
              />
            </div>
          </div>

          <div className="exam-form-field">
            <label htmlFor="collegeCode" className="exam-form-label">
              College Code <span className="exam-form-required">*</span>
            </label>
            <div className="exam-form-input-wrapper">
              <Building size={16} className="exam-form-input-icon" />
              <input
                type="text"
                id="collegeCode"
                name="collegeCode"
                value={formData.collegeCode}
                onChange={handleInputChange}
                placeholder="e.g., INST123"
                maxLength={8}
                className="exam-form-input"
                disabled={loading}
                required
              />
            </div>
            <div className="exam-form-helper-text">
              Enter the 6-8 character code provided by your institution
            </div>
            {collegeStatus === "checking" && (
              <div className="exam-form-helper-text">Checking college code...</div>
            )}
            {collegeStatus === "valid" && (
              <div style={{ color: "green", fontSize: "12px" }}>
                ✔ Valid college code
              </div>
            )}
            {collegeStatus === "invalid" && (
              <div className="exam-form-error-text">
                ❌ Invalid college code
              </div>
            )}
            {collegeError && (
              <div className="exam-form-error-text">{collegeError}</div>
            )}
          </div>
        </div>
      </div>

      <div className="exam-form-actions exam-form-actions-between">
        <button
          onClick={() => setCurrentStep(1)}
          className="exam-form-button exam-form-button-secondary"
        >
          <ArrowLeft size={18} />
          <span>Back</span>
        </button>

        <button
          onClick={handleContinueToConfirmation}
          disabled={!isRegistrationValid || loading}
          className="exam-form-button exam-form-button-primary"
        >
          <span>Continue to Confirmation</span>
          <ChevronRight size={18} />
        </button>
      </div>
    </div>
  );

  // Step 3: Confirmation
  const renderConfirmationStep = () => (
    <div className="exam-form-section">
      <div className="exam-form-section-header">
        <h2 className="exam-form-section-title">Confirm Registration</h2>
        <p className="exam-form-section-subtitle">Please verify your details before starting the exam</p>
      </div>

      <div className="exam-form-confirmation-card">
        <h3 className="exam-form-confirmation-title">Registration Summary</h3>
        
        <div className="exam-form-confirmation-grid">
          <div className="exam-form-confirmation-item">
            <span className="exam-form-confirmation-label">Full Name</span>
            <span className="exam-form-confirmation-value">{formData.fullName}</span>
          </div>
          
          <div className="exam-form-confirmation-item">
            <span className="exam-form-confirmation-label">Roll Number</span>
            <span className="exam-form-confirmation-value">{formData.rollNumber}</span>
          </div>
          
          <div className="exam-form-confirmation-item">
            <span className="exam-form-confirmation-label">Email Address</span>
            <span className="exam-form-confirmation-value">{formData.email}</span>
          </div>
          
          <div className="exam-form-confirmation-item">
            <span className="exam-form-confirmation-label">College Code</span>
            <span className="exam-form-confirmation-value">{formData.collegeCode}</span>
          </div>
          
          <div className="exam-form-confirmation-item exam-form-confirmation-item-full">
            <span className="exam-form-confirmation-label">Exam Name</span>
            <span className="exam-form-confirmation-value">{examInfo.name}</span>
          </div>
        </div>
      </div>

      <div className="exam-form-notice">
        <Info size={16} />
        <p>By starting the exam, you confirm that all provided information is accurate and you agree to follow all exam rules and regulations.</p>
      </div>

      <div className="exam-form-actions exam-form-actions-between">
        <button
          onClick={() => setCurrentStep(2)}
          className="exam-form-button exam-form-button-secondary"
          disabled={loading}
        >
          <ArrowLeft size={18} />
          <span>Back to Edit</span>
        </button>

        <button
          onClick={handleStartExam}
          disabled={loading}
          className="exam-form-button exam-form-button-primary exam-form-button-large"
        >
          {loading ? (
            <>
              <span className="exam-form-spinner"></span>
              <span>Verifying...</span>
            </>
          ) : (
            <>
              <span>Start Exam</span>
              <ArrowRight size={18} />
            </>
          )}
        </button>
      </div>
    </div>
  );

  return (
    <div className="exam-form-container">
      <div className="exam-form-header">
        <h1 className="exam-form-title">Online Examination Portal</h1>
        <p className="exam-form-subtitle">Government of India - Ministry of Education</p>
      </div>

      {ProgressIndicator}

      <div className="exam-form-main">
        <div className="exam-form-content">
          {currentStep === 1 && renderInstructionsStep()}
          {currentStep === 2 && renderRegistrationStep()}
          {currentStep === 3 && renderConfirmationStep()}
        </div>
        
        {ExamInfoSidebar}
      </div>
    </div>
  );
};

export default TestSetup;